import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Truck, Search, MapPin, ArrowRight, Check, Package } from "lucide-react";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["recibido", "despacho", "en_aduana", "en_reparto", "entregado"];

export default function PublicTrack() {
  const params = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  // Localised label for every possible flow state we might see on this page.
  // Falls back to the raw key so unmapped values still show up.
  const stateLabel = (key) => {
    if (!key) return "";
    const map = {
      recibido: t("state_received") || "Recibido",
      despacho: t("status_despacho") || "Despachado",
      en_aduana: t("status_en_aduana") || "En aduana",
      en_reparto: t("status_en_reparto") || "En reparto",
      entregado: t("status_entregado") || t("flow_entregado") || "Entregado",
      etiqueta_creada: t("state_label_created") || "Etiqueta creada",
      recibido_almacen: t("state_received_warehouse") || "Recibido en almacén",
      recibido_oficina: t("state_received_office") || "Recibido en oficina",
      etiquetado: t("state_labeled") || "Etiquetado",
      clasificado: t("flow_clasificado") || "Clasificado",
      en_salida: t("flow_en_salida") || "En salida",
      transito_hub: t("flow_transito_hub") || "En tránsito al hub",
      recibido_hub: t("flow_recibido_hub") || "Recibido en hub",
      verificado_hub: t("flow_verificado_hub") || "Verificado",
      en_master: t("flow_en_master") || "En master",
      master_cerrado: t("flow_master_cerrado") || "Master cerrado",
      anadido_master: t("state_added_master") || "Añadido al MASTER",
      anadido_salida: t("state_added_office_exit") || "Añadido a salida",
      en_transito: t("state_in_transit") || "En tránsito al destino",
      recibido_destino: t("state_received_destination") || "Recibido en destino",
    };
    return map[key] || key.replace(/_/g, " ");
  };
  const [code, setCode] = useState(params.code || "");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const search = async (c) => {
    setBusy(true); setError(""); setData(null);
    try {
      const { data } = await axios.get(`${API}/public/track/${encodeURIComponent(c)}`);
      setData(data);
      nav(`/track/${c}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "No encontrado");
    } finally { setBusy(false); }
  };

  React.useEffect(() => {
    if (params.code) search(params.code);
  // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-track">
      <header className="bg-[#0B0F19] text-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="bg-blue-600 p-2 rounded-lg"><Truck className="w-5 h-5" /></div>
          <div>
            <div className="font-display font-bold">MEOS CARGO</div>
            <div className="text-xs text-slate-400">{t("public_tracking")}</div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <form onSubmit={(e)=>{e.preventDefault(); if (code) search(code);}} className="flex gap-2 mb-8">
          <input
            data-testid="track-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="MEOS-2026-000123"
            className="flex-1 px-4 py-3 rounded-lg border border-slate-300 outline-none focus:border-red-600 font-mono"
          />
          <button data-testid="track-btn" disabled={busy || !code} className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 rounded-lg flex items-center gap-2 disabled:opacity-60">
            <Search className="w-4 h-4" /> {t("track") || "Rastrear"}
          </button>
        </form>

        {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

        {data && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-mono text-lg font-bold">{data.tracking_code}</div>
                {data.package_code && (
                  <div className="mt-1 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 text-xs" data-testid="pt-package">
                    <Package className="w-3 h-3 text-amber-700" />
                    <span className="text-amber-800">{t("package_code")}:</span>
                    <span className="font-mono font-bold text-amber-900">{data.package_code}</span>
                  </div>
                )}
                <div className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                  <MapPin className="w-3 h-3" /> {data.origen}
                  <ArrowRight className="w-3 h-3" />
                  {data.destino}
                </div>
                {data.descripcion && <p className="text-sm text-slate-600 mt-1">{data.descripcion}</p>}
                {data.voyage && (
                  <div className="mt-2 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-xs" data-testid="pt-voyage">
                    <span className="font-mono font-bold text-blue-700">{data.voyage.codigo}</span>
                    <span className="text-blue-600">{data.voyage.puerto_origen} → {data.voyage.puerto_destino}</span>
                    {data.voyage.salida && <span className="text-blue-500">· {data.voyage.salida}</span>}
                    {data.voyage.vessel_number && <span className="text-blue-500 font-mono">· {data.voyage.vessel_number}</span>}
                  </div>
                )}
                {data.container_number && !data.voyage && (
                  <div className="mt-2 inline-flex items-center gap-2 bg-slate-100 rounded-full px-3 py-1 text-xs font-mono text-slate-700">
                    Container: {data.container_number}
                  </div>
                )}
              </div>
              <span className="px-4 py-2 rounded-full bg-blue-600 text-white font-semibold text-sm uppercase tracking-wider">
                {stateLabel(data.estado)}
              </span>
            </div>

            <div className="grid grid-cols-5 gap-2">
              {STATUSES.map((st, i) => {
                const idx = STATUSES.indexOf(data.estado);
                const done = i <= idx;
                return (
                  <div key={st} className="flex flex-col items-center gap-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      done ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-400 border border-slate-200"
                    }`}>
                      {done ? <Check className="w-5 h-5" /> : <Package className="w-4 h-4" />}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider text-center ${done ? "font-bold text-slate-900" : "text-slate-400"}`}>
                      {stateLabel(st)}
                    </span>
                  </div>
                );
              })}
            </div>

            {(data.fecha_recepcion || data.fecha_salida) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="pt-dates">
                {data.fecha_recepcion && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{t("reception_date")}</div>
                    <div className="text-sm font-semibold tabular-nums text-emerald-900">{new Date(data.fecha_recepcion).toLocaleString()}</div>
                  </div>
                )}
                {data.fecha_salida && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700">{t("departure_date")}</div>
                    <div className="text-sm font-semibold tabular-nums text-blue-900">{new Date(data.fecha_salida).toLocaleString()}</div>
                  </div>
                )}
              </div>
            )}

            {data.paquetes && data.paquetes.length > 0 && (
              <div className="pt-2" data-testid="pt-packages">
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">{t("packages_in_master")} ({data.paquetes.length})</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[420px]">
                    <thead className="text-[10px] uppercase text-slate-400">
                      <tr><th className="p-2 text-start">{t("package_code")}</th><th className="p-2 text-start">{t("description")}</th><th className="p-2 text-end">kg</th><th className="p-2 text-start">{t("reception_date")}</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.paquetes.map(p => (
                        <tr key={p.code} className={p.code === data.package_code ? "bg-amber-50" : ""}>
                          <td className="p-2 font-mono font-semibold">{p.code}</td>
                          <td className="p-2 text-slate-600">{p.descripcion || "—"}</td>
                          <td className="p-2 text-end tabular-nums">{p.peso || 0}</td>
                          <td className="p-2 tabular-nums text-slate-600">
                            {p.fecha_recepcion ? new Date(p.fecha_recepcion).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {data.historial && data.historial.length > 0 && (
              <div className="pt-4 border-t border-slate-100">
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">{t("history")}</p>
                <ul className="space-y-2">
                  {data.historial.slice().reverse().map((h, i) => (
                    <li key={`${h.fecha}-${h.estado}-${i}`} className="flex items-start gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 shrink-0"></div>
                      <div className="flex-1">
                        <div className="font-semibold">{stateLabel(h.estado)}</div>
                        <div className="text-xs text-slate-500 tabular-nums">{new Date(h.fecha).toLocaleString()}</div>
                        {h.nota && <div className="text-xs text-slate-600 mt-0.5">{h.nota}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data.observaciones && data.observaciones.length > 0 && (
              <div className="pt-4 border-t border-slate-100" data-testid="pt-notes">
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">{t("package_notes")}</p>
                <ul className="space-y-2">
                  {data.observaciones.map((n, i) => (
                    <li key={`${n.code || ""}-${n.created_at || i}`} className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                      <p className="text-slate-700">{n.texto}</p>
                      <p className="text-[11px] text-slate-400 mt-1 tabular-nums">
                        {n.code} · {new Date(n.created_at).toLocaleString()}{n.created_by_name ? ` · ${n.created_by_name}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
